/**
 * CompetitionsSplitTable.jsx
 * Shared Tournaments/Leagues table pair, used by both Home and Create &
 * Manage instead of each view keeping its own copy of one flat table mixing
 * both types together. A tournament is a single bounded event the athlete
 * prepares TOWARD (a start-date countdown matters, win% doesn't — one loss
 * ends it); a league is judged over a longer stretch (win% and its date
 * range both matter). `practice`-type competitions deliberately get no
 * table here — see docs_zenanalytics/25-STRUCTURE-AND-CAREER-RECORD-
 * PLANNING-2026-10-28.md, "no dedicated table" decision.
 */
import React from 'react';
import { formatDate as formatDateLocaleAware } from '../utils/dateLocal';

const C = { text: '#e2e8f0', muted: '#94a3b8', accent: '#10b981', purple: '#a78bfa', dim: '#64748b' };

function compIcon(type) {
  return type === 'league' ? '🏅' : type === 'tournament' ? '🏆' : '🎯';
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr.slice(0, 10) + 'T00:00:00');
  return Math.round((d - today) / 86400000);
}

function compStats(c, plans) {
  const compPlans = plans.filter(p => p.competition_id === c.id);
  const matches = compPlans.filter(p => ['match', 'competition'].includes(p.type) && p.result);
  const wins = matches.filter(p => p.result === 'win').length;
  const total = c.format_meta?.total_matches_planned || compPlans.length;
  const avgOf = (field) => {
    const vals = matches.filter(p => p[field]).map(p => p[field]);
    return vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : null;
  };
  return { matches, wins, total, avgPerf: avgOf('performance_score'), avgPlanOutcome: avgOf('game_plan_adherence') };
}

function TableBlock({ title, headers, rows, emptyLabel }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ color: C.muted, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
        {title}
      </div>
      {rows.length === 0 ? (
        <div style={{ color: C.dim, fontSize: 12.5, fontStyle: 'italic' }}>{emptyLabel}</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(56,189,248,0.45)' }}>
                {headers.map((h, i) => (
                  <th key={h} style={{ textAlign: i === 0 ? 'left' : 'right', padding: '4px 6px', color: C.muted, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>{rows}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function CompetitionsSplitTable({ competitions, plans, onRowClick, canManage }) {
  const tournaments = competitions.filter(c => c.type === 'tournament');
  const leagues = competitions.filter(c => c.type === 'league');
  const rowStyle = { borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: canManage ? 'pointer' : 'default' };

  const tournamentRows = tournaments.map(c => {
    const { matches, total, avgPerf, avgPlanOutcome } = compStats(c, plans);
    const d = daysUntil(c.start_date);
    const progress = matches.length > 0
      ? `Played ${matches.length}/${total}`
      : (d === null ? '—' : d > 0 ? `Starts in ${d}d` : d === 0 ? 'Starts today' : 'In progress');
    return (
      <tr key={c.id} onClick={() => canManage && onRowClick(c)} style={rowStyle}>
        <td style={{ padding: '7px 6px', color: C.text, fontWeight: 600 }}>
          {compIcon(c.type)} {c.name}
          {c.status === 'archived' && <span style={{ color: C.muted, fontWeight: 400 }}> · archived</span>}
        </td>
        <td style={{ padding: '7px 6px', textAlign: 'right', color: C.text }}>{progress}</td>
        <td style={{ padding: '7px 6px', textAlign: 'right', color: C.accent }}>{avgPerf ? `${avgPerf}★` : '—'}</td>
        <td style={{ padding: '7px 6px', textAlign: 'right', color: C.purple }}>{avgPlanOutcome ? `${avgPlanOutcome}★` : '—'}</td>
      </tr>
    );
  });

  const leagueRows = leagues.map(c => {
    const { matches, wins, total, avgPerf, avgPlanOutcome } = compStats(c, plans);
    const dateRange = c.start_date ? `${formatDateLocaleAware(c.start_date)}${c.end_date ? ' – ' + formatDateLocaleAware(c.end_date) : ''}` : null;
    return (
      <tr key={c.id} onClick={() => canManage && onRowClick(c)} style={rowStyle}>
        <td style={{ padding: '7px 6px', color: C.text, fontWeight: 600 }}>
          <div>{compIcon(c.type)} {c.name}{c.status === 'archived' && <span style={{ color: C.muted, fontWeight: 400 }}> · archived</span>}</div>
          {dateRange && <div style={{ color: C.dim, fontSize: 10, fontWeight: 400 }}>{dateRange}</div>}
        </td>
        <td style={{ padding: '7px 6px', textAlign: 'right', color: C.text }}>{matches.length}/{total}</td>
        <td style={{ padding: '7px 6px', textAlign: 'right', color: C.text }}>{matches.length ? `${Math.round(wins / matches.length * 100)}%` : '—'}</td>
        <td style={{ padding: '7px 6px', textAlign: 'right', color: C.accent }}>{avgPerf ? `${avgPerf}★` : '—'}</td>
        <td style={{ padding: '7px 6px', textAlign: 'right', color: C.purple }}>{avgPlanOutcome ? `${avgPlanOutcome}★` : '—'}</td>
      </tr>
    );
  });

  return (
    <>
      <TableBlock title="Tournaments" headers={['Competition', 'Starts in / Played', 'Avg Perf', 'Plan Outcome']}
        rows={tournamentRows} emptyLabel="No tournaments yet." />
      <TableBlock title="🏅 Leagues" headers={['Competition', 'Played', 'Win%', 'Avg Perf', 'Plan Outcome']}
        rows={leagueRows} emptyLabel="No leagues yet." />
    </>
  );
}
